// Fetch-based API service for Lead Settings, following the request pattern established in
// leadCaptureApi.ts. Backs the branding configuration shared by the Lead Capture Form and
// Newsletter Subscription Form (public plugins/embeds).
import { SERVICES_API_BASE_URL } from './apiConfig';
import { LeadSettingsDto, PublicLeadSettingsDto, PublicNewsletterSettingsDto } from '../types/leadSettings';

const API_BASE = `${(SERVICES_API_BASE_URL || '/api').replace(/\/+$/, '')}`;

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...getAuthHeaders(),
      ...(init.headers ?? {}),
    },
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}.`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export const leadSettingsApi = {
  /**
   * Get the authenticated company's Lead Settings.
   * GET /api/LeadSettings/me
   */
  getSettings: async (): Promise<LeadSettingsDto> => {
    return request<LeadSettingsDto>('/api/LeadSettings/me');
  },

  /**
   * Create or update the authenticated company's Lead Settings.
   * POST /api/LeadSettings/me
   */
  saveSettings: async (data: LeadSettingsDto): Promise<LeadSettingsDto> => {
    return request<LeadSettingsDto>('/api/LeadSettings/me', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Public branding lookup for the Lead Capture Form embed/plugin, mirroring publicJobBoardApi's pattern.
   * NOTE: placeholder endpoint — connect to the real public route once it is exposed by the API.
   * GET /api/public/lead-settings/{slug}
   */
  getPublicSettings: async (slug: string): Promise<PublicLeadSettingsDto> => {
    const response = await fetch(`${API_BASE}/api/LeadSettings/public/${slug}`);

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}.`);
    }

    return response.json() as Promise<PublicLeadSettingsDto>;
  },

  /**
   * Public branding lookup for the Newsletter Subscription Form embed/plugin, keyed by its own slug.
   * NOTE: placeholder endpoint — connect to the real public route once it is exposed by the API.
   * GET /api/public/newsletter-settings/{slug}
   */
  getPublicNewsletterSettings: async (slug: string): Promise<PublicNewsletterSettingsDto> => {
    const response = await fetch(`${API_BASE}/api/LeadSettings/public/${slug}`);

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}.`);
    }

    return response.json() as Promise<PublicNewsletterSettingsDto>;
  },
};
